import { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useScoopAdmin } from '../../../api/hooks/scoopAdmin/useScoopAdmin'
import {
    ScoopButton,
    ScoopText,
    ScoopSelector,
    ScoopCheckbox,
    ScoopTable,
    ScoopTab,
    ScoopSpinner,
    ScoopTextField,
    ScoopScreenLayout,
    useToast,
} from '../../ui'
import styles from './ScoopAdmin.module.scss'

const DEBOUNCE_MS = 400

const useDebounce = (value, delay) => {
    const [debounced, setDebounced] = useState(value)
    const timerRef = useRef(null)

    useEffect(() => {
        timerRef.current = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(timerRef.current)
    }, [value, delay])

    return debounced
}

const tabs = [
    { key: 'users', label: 'Users' },
    { key: 'workspaces', label: 'Workspaces' },
]

export const ScoopAdmin = () => {
    const token = useSelector((state) => state.auth.token)
    const userID = useSelector((state) => state.auth.userID)

    const [activeTab, setActiveTab] = useState('users')
    const [targetUser, setTargetUser] = useState(null)
    const targetUserID = targetUser?.userID
    const targetUserName = targetUser?.userName

    const [userInputValue, setUserInputValue] = useState('')
    const [workspaceInputValue, setWorkspaceInputValue] = useState('')
    const [workspaceSearchBy, setWorkspaceSearchBy] = useState('name')
    const [selectedWorkspaceID, setSelectedWorkspaceID] = useState('')
    const [userRole, setUserRole] = useState('Member')
    const [newWorkspaceName, setNewWorkspaceName] = useState('')
    const [ignoreTourAndDelete, setIgnoreTourAndDelete] = useState(true)
    const [userWorkspacesFilter, setUserWorkspacesFilter] = useState('')

    const toast = useToast()

    // Workspaces tab state
    const [cacheWorkspaceInputValue, setCacheWorkspaceInputValue] = useState('')
    const [selectedCacheWorkspace, setSelectedCacheWorkspace] = useState(null)
    const [selectedInbox, setSelectedInbox] = useState('')
    const [cacheWorkspaceSearchBy, setCacheWorkspaceSearchBy] = useState('name')
    const [cacheIgnoreTourAndDelete, setCacheIgnoreTourAndDelete] = useState(true)

    const debouncedUserSearch = useDebounce(userInputValue, DEBOUNCE_MS)
    const debouncedWorkspaceSearch = useDebounce(workspaceInputValue, DEBOUNCE_MS)
    const debouncedCacheWorkspaceSearch = useDebounce(cacheWorkspaceInputValue, DEBOUNCE_MS)

    const {
        users,
        loadingUsers,
        workspaces,
        loadingWorkspaces,
        userWorkspaces,
        addUser,
        removeUser,
        createNewWorkspace,
        isCreatingWorkspace,
        clearCache,
        isClearingCache,
        workspaceInboxes,
        loadingInboxes,
    } = useScoopAdmin(
        debouncedUserSearch,
        activeTab === 'users' ? debouncedWorkspaceSearch : debouncedCacheWorkspaceSearch,
        targetUserID,
        activeTab === 'users' ? workspaceSearchBy : cacheWorkspaceSearchBy,
        selectedCacheWorkspace?.workspaceID
    )

    const filterWorkspaces = (list, shouldFilter) => {
        if (!shouldFilter) return list
        return list.filter((ws) => {
            const name = ws.name.toLowerCase()
            return (
                !name.includes('canva tour') &&
                !name.includes('tour scoop') &&
                !name.includes("delete's") &&
                !name.includes('test space')
            )
        })
    }

    const filteredWorkspaces = filterWorkspaces(workspaces, ignoreTourAndDelete)
    const filteredCacheWorkspaces = filterWorkspaces(workspaces, cacheIgnoreTourAndDelete)

    const filteredUserWorkspaces = userWorkspacesFilter
        ? userWorkspaces.filter((ws) => {
              const filter = userWorkspacesFilter.toLowerCase()
              return (
                  ws.name?.toLowerCase().includes(filter) ||
                  ws.workspaceID?.toLowerCase().includes(filter)
              )
          })
        : userWorkspaces

    const handleOpenAILogs = () => {
        if (!token) {
            toast.error('No valid session found!')
            return
        }
        const logsURL = `https://scooplogs.web.app?jwt=${token}`
        window.open(logsURL, '_blank')
    }

    const handleLogInAsUser = () => {
        const personalWorkspaceID = targetUser?.personalWorkspace
        const personalWorkspace = userWorkspaces.find(
            (ws) => ws.workspaceID === personalWorkspaceID
        )
        const fallbackWorkspace = userWorkspaces[0]
        const targetWorkspace = personalWorkspace || fallbackWorkspace

        localStorage.setItem('userID', targetUserID)
        localStorage.setItem('userName', targetUserName)
        localStorage.setItem('userEmail', targetUserName)
        localStorage.setItem(
            'CognitoIdentityServiceProvider.76atr62rn4iuk8uhj8r8gt39ms.LastAuthUser',
            targetUserID
        )

        if (targetWorkspace) {
            localStorage.setItem('workspaceID', targetWorkspace.workspaceID)
            localStorage.setItem('workspaceName', targetWorkspace.name)
            const workspaceType = personalWorkspace ? 'personal' : 'first available'
            toast.info(
                `Logging in as ${targetUserName} → ${targetWorkspace.name} (${workspaceType})`
            )
        } else {
            localStorage.setItem('workspaceID', '')
            localStorage.setItem('workspaceName', '')
            toast.warning(`Logging in as ${targetUserName} (no workspaces found)`)
        }

        setTimeout(() => {
            window.location.href = window.location.origin
        }, 500)
    }

    const handleAddUserToWorkspace = () => {
        addUser({
            workspaceID: selectedWorkspaceID,
            userID: targetUserID,
            role: userRole,
            onSuccess: () => {
                toast.success(`Added ${targetUserName} to ${selectedWorkspaceID}`)
            },
            onError: () => {
                toast.error('Failed to add user to workspace')
            },
        })
    }

    const handleRemoveUserFromWorkspace = (workspaceID) => {
        removeUser({
            workspaceID,
            userID: targetUserID,
            onSuccess: () => {
                toast.success(`Removed ${targetUserName} from ${workspaceID}`)
            },
            onError: () => {
                toast.error('Failed to remove user from workspace')
            },
        })
    }

    const handleCreateWorkspace = () => {
        toast.info(`Stay put, creating new workspace ${newWorkspaceName}`)
        createNewWorkspace({
            workspaceName: newWorkspaceName,
            userName: targetUserName,
            userID: targetUserID,
            onSuccess: (response) => {
                toast.success(
                    `Workspace ${newWorkspaceName} created successfully as: ${response.workspaceID}`
                )
                setNewWorkspaceName('')
            },
            onError: () => {
                toast.error('Failed to create workspace')
            },
        })
    }

    const handleClearCache = () => {
        clearCache({
            workspaceID: selectedCacheWorkspace.workspaceID,
            userID,
            inboxID: selectedInbox || undefined,
            onSuccess: (response) => {
                toast.success(`Cleared cache for ${response.inboxesCleared || 1} inbox(es)`)
            },
            onError: () => {
                toast.error('Failed to clear cache')
            },
        })
    }

    // Ensure selected user is always in the options list
    const userOptions = (() => {
        const options = users.map((u) => ({
            label: u.userName,
            value: u.userID,
        }))
        // Add selected user to options if not already present
        if (targetUser && !users.find((u) => u.userID === targetUserID)) {
            options.unshift({
                label: targetUserName,
                value: targetUserID,
            })
        }
        return options
    })()

    const workspaceOptions = filteredWorkspaces.map((ws) => ({
        label: `${ws.name} (${ws.workspaceID})`,
        value: ws.workspaceID,
    }))

    const cacheWorkspaceOptions = filteredCacheWorkspaces.map((ws) => ({
        label: `${ws.name} (${ws.workspaceID})`,
        value: ws.workspaceID,
    }))

    const inboxOptions = [
        { label: 'All Inboxes', value: '' },
        ...workspaceInboxes.map((inbox) => ({
            label: inbox.label || inbox.name || inbox.inboxID,
            value: inbox.inboxID,
        })),
    ]

    const tableColumns = [
        { field: 'name', header: 'Name', flex: 1 },
        { field: 'workspaceID', header: 'Workspace ID', flex: 1 },
        {
            field: 'actions',
            header: 'Actions',
            width: 120,
            render: ({ row }) => (
                <ScoopButton
                    variant="danger"
                    size="xs"
                    onClick={() => handleRemoveUserFromWorkspace(row.workspaceID)}
                >
                    Remove
                </ScoopButton>
            ),
        },
    ]

    const handleUserSelect = (e) => {
        const selectedUserID = e.target.value
        const selectedUser = users.find((u) => u.userID === selectedUserID)
        if (selectedUser) {
            setTargetUser(selectedUser)
            setUserWorkspacesFilter('')
        }
    }

    const handleWorkspaceSelect = (e) => {
        setSelectedWorkspaceID(e.target.value)
    }

    const handleCacheWorkspaceSelect = (e) => {
        const workspaceID = e.target.value
        const workspace = workspaces.find((ws) => ws.workspaceID === workspaceID)
        if (workspace) {
            setSelectedCacheWorkspace(workspace)
            setSelectedInbox('')
        }
    }

    return (
        <ScoopScreenLayout
            title="Scoop Admin"
            headerActions={
                <ScoopButton variant="danger" onClick={handleOpenAILogs}>
                    AI Logs
                </ScoopButton>
            }
            contentClassName={styles.content}
        >
            <ScoopTab
                tabs={tabs}
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="underline"
                fullWidth={false}
            />

            {activeTab === 'users' && (
                <div className={styles.tabContent}>
                    <div className={styles.section}>
                        <ScoopText variant="subtitle" weight="semibold">
                            Search Users
                        </ScoopText>
                        <div className={styles.userSearchContainer}>
                            <ScoopSelector
                                label="Search users (min 3 characters)"
                                value={targetUserID || ''}
                                onChange={handleUserSelect}
                                options={userOptions}
                                searchable
                                searchPlaceholder="Type to search users..."
                                placeholder="Select a user..."
                                filterOptions={(opts, query) => {
                                    setUserInputValue(query)
                                    return opts
                                }}
                                noResultsText={
                                    loadingUsers
                                        ? 'Loading...'
                                        : userInputValue.length < 3
                                          ? 'Type at least 3 characters'
                                          : 'No users found'
                                }
                            />
                            {loadingUsers && (
                                <span className={styles.spinnerInline}>
                                    <ScoopSpinner size={16} />
                                </span>
                            )}
                        </div>
                    </div>

                    {targetUserID && (
                        <>
                            <div className={styles.loginAsSection}>
                                <ScoopButton variant="default" onClick={handleLogInAsUser}>
                                    Login as {targetUserName}
                                </ScoopButton>
                                {userWorkspaces.length > 0 && (
                                    <ScoopText variant="body" color="secondary">
                                        →{' '}
                                        {targetUser?.personalWorkspace
                                            ? `Personal: ${userWorkspaces.find((ws) => ws.workspaceID === targetUser.personalWorkspace)?.name || targetUser.personalWorkspace}`
                                            : `First workspace: ${userWorkspaces[0]?.name}`}
                                    </ScoopText>
                                )}
                            </div>
                            <div className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <ScoopText variant="subtitle" weight="semibold">
                                        Workspaces <u>{targetUserName}</u> has access to (
                                        {userWorkspaces.length})
                                    </ScoopText>
                                    <div className={styles.filterInput}>
                                        <ScoopTextField
                                            placeholder="Filter by name or ID..."
                                            value={userWorkspacesFilter}
                                            onChange={(e) =>
                                                setUserWorkspacesFilter(e.target.value)
                                            }
                                        />
                                    </div>
                                </div>
                                <div className={styles.tableContainer}>
                                    <ScoopTable
                                        columns={tableColumns}
                                        rows={filteredUserWorkspaces}
                                        emptyMessage="No workspaces found"
                                        height={400}
                                        domLayout="normal"
                                    />
                                </div>
                            </div>

                            <div className={styles.section}>
                                <ScoopText variant="subtitle" weight="semibold">
                                    Give access to workspace
                                </ScoopText>
                                <div className={styles.formRow}>
                                    <div className={styles.roleSelector}>
                                        <ScoopSelector
                                            label="Role"
                                            value={userRole}
                                            onChange={(e) => setUserRole(e.target.value)}
                                            options={[
                                                { label: 'Owner', value: 'Owner' },
                                                { label: 'Admin', value: 'Admin' },
                                                { label: 'Member', value: 'Member' },
                                            ]}
                                            size="sm"
                                        />
                                    </div>
                                    <div className={styles.searchBySelector}>
                                        <ScoopSelector
                                            label="Search by"
                                            value={workspaceSearchBy}
                                            onChange={(e) => {
                                                setWorkspaceSearchBy(e.target.value)
                                                setWorkspaceInputValue('')
                                            }}
                                            options={[
                                                { label: 'Name', value: 'name' },
                                                { label: 'ID', value: 'id' },
                                            ]}
                                            size="sm"
                                        />
                                    </div>
                                    <div className={styles.workspaceSearchField}>
                                        <ScoopSelector
                                            label={`Search workspaces by ${workspaceSearchBy} (min 2 characters)`}
                                            value={selectedWorkspaceID}
                                            onChange={handleWorkspaceSelect}
                                            options={workspaceOptions}
                                            searchable
                                            searchPlaceholder="Type to search..."
                                            placeholder="Select a workspace..."
                                            filterOptions={(opts, query) => {
                                                setWorkspaceInputValue(query)
                                                return opts
                                            }}
                                            noResultsText={
                                                loadingWorkspaces
                                                    ? 'Loading...'
                                                    : workspaceInputValue.length < 2
                                                      ? 'Type at least 2 characters'
                                                      : 'No workspaces found'
                                            }
                                        />
                                    </div>
                                    <ScoopCheckbox
                                        checked={ignoreTourAndDelete}
                                        onChange={(e) => setIgnoreTourAndDelete(e.target.checked)}
                                        label="Ignore delete and tour workspaces"
                                    />
                                </div>
                                <ScoopButton onClick={handleAddUserToWorkspace}>
                                    Give {targetUserName} access to {selectedWorkspaceID || '...'}
                                </ScoopButton>
                            </div>

                            <div className={styles.section}>
                                <ScoopText variant="subtitle" weight="semibold">
                                    Create New Workspace
                                </ScoopText>
                                <div className={styles.newWorkspaceForm}>
                                    <ScoopTextField
                                        label="New Workspace Name"
                                        value={newWorkspaceName}
                                        onChange={(e) => setNewWorkspaceName(e.target.value)}
                                        placeholder="Enter workspace name"
                                    />
                                    <ScoopButton
                                        onClick={handleCreateWorkspace}
                                        disabled={!newWorkspaceName || isCreatingWorkspace}
                                        loading={isCreatingWorkspace}
                                    >
                                        Create New Workspace
                                    </ScoopButton>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {activeTab === 'workspaces' && (
                <div className={styles.tabContent}>
                    <div className={styles.workspaceSection}>
                        <div className={styles.sectionTitleGroup}>
                            <ScoopText variant="subtitle" weight="semibold">
                                Workspace Management
                            </ScoopText>
                            <ScoopText variant="body" color="secondary">
                                Search for a workspace to clear its AI cache.
                            </ScoopText>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.searchBySelector}>
                                <ScoopSelector
                                    label="Search by"
                                    value={cacheWorkspaceSearchBy}
                                    onChange={(e) => {
                                        setCacheWorkspaceSearchBy(e.target.value)
                                        setCacheWorkspaceInputValue('')
                                        setSelectedCacheWorkspace(null)
                                        setSelectedInbox('')
                                    }}
                                    options={[
                                        { label: 'Name', value: 'name' },
                                        { label: 'ID', value: 'id' },
                                    ]}
                                    size="sm"
                                />
                            </div>
                            <div className={styles.workspaceSearchField}>
                                <ScoopSelector
                                    label={`Search workspaces by ${cacheWorkspaceSearchBy} (min 2 characters)`}
                                    value={selectedCacheWorkspace?.workspaceID || ''}
                                    onChange={handleCacheWorkspaceSelect}
                                    options={cacheWorkspaceOptions}
                                    searchable
                                    searchPlaceholder="Type to search..."
                                    placeholder="Select a workspace..."
                                    filterOptions={(opts, query) => {
                                        setCacheWorkspaceInputValue(query)
                                        return opts
                                    }}
                                    noResultsText={
                                        loadingWorkspaces
                                            ? 'Loading...'
                                            : cacheWorkspaceInputValue.length < 2
                                              ? 'Type at least 2 characters'
                                              : 'No workspaces found'
                                    }
                                />
                            </div>
                            <ScoopCheckbox
                                checked={cacheIgnoreTourAndDelete}
                                onChange={(e) => setCacheIgnoreTourAndDelete(e.target.checked)}
                                label="Ignore delete and tour workspaces"
                            />
                        </div>

                        {selectedCacheWorkspace && (
                            <div className={styles.selectedWorkspace}>
                                <ScoopText variant="body">
                                    Selected: <strong>{selectedCacheWorkspace.name}</strong> (
                                    {selectedCacheWorkspace.workspaceID})
                                </ScoopText>

                                <div className={styles.cacheActions}>
                                    <div className={styles.inboxSelector}>
                                        <ScoopSelector
                                            label="Inbox (optional)"
                                            value={selectedInbox}
                                            onChange={(e) => setSelectedInbox(e.target.value)}
                                            options={inboxOptions}
                                            disabled={loadingInboxes}
                                            size="sm"
                                        />
                                    </div>
                                    <ScoopButton
                                        variant="danger"
                                        onClick={handleClearCache}
                                        disabled={isClearingCache}
                                        loading={isClearingCache}
                                    >
                                        Clear AI Cache
                                    </ScoopButton>
                                </div>

                                {loadingInboxes && (
                                    <div className={styles.loadingRow}>
                                        <ScoopSpinner size={20} />
                                        <ScoopText variant="body" color="secondary">
                                            Loading inboxes...
                                        </ScoopText>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </ScoopScreenLayout>
    )
}
